import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import { DEFAULT_LOCATION_ID } from '../../platform/events/operationEvents';
import RepairCategories from '../RepairCategories';

const client = generateClient();

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px',
    padding: '24px', width: '640px', maxWidth: '95vw',
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(15,23,42,0.18)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #e2e8f0',
  },
  title: { fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#f59e0b' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
  label: { display: 'block', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '5px' },
  input: {
    width: '100%', padding: '8px 10px', background: '#f8fafc',
    border: '1px solid #cbd5e1', borderRadius: '6px', color: '#0f172a',
    fontSize: '13px', boxSizing: 'border-box', outline: 'none',
  },
  field: { marginBottom: '14px' },
  row: { display: 'flex', gap: '12px', marginBottom: '14px' },
  err: { fontSize: '11px', color: '#ef4444', marginTop: '3px' },
};

// ─── Add / Edit form ──────────────────────────────────────────────────────────

function TechForm({ onClose, onSave, existing, editMode = false, techData = null }) {
  const [form, setForm] = useState({
    number: techData?.number ?? '',
    name:   techData?.name   ?? '',
    specialties: techData?.specialties ?? [],
  });
  const [errors, setErrors] = useState({});
  const [pickingCategory, setPickingCategory] = useState(false);

  function change(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  }

  function validate() {
    const errs = {};
    if (!editMode) {
      if (!form.number.trim()) errs.number = 'Required';
      else if (existing[form.number] && form.number !== techData?.number) errs.number = 'Number already in use';
    }
    if (!form.name.trim()) errs.name = 'Required';
    if (!form.specialties.length) errs.specialties = 'Add at least one specialty';
    setErrors(errs);
    return !Object.keys(errs).length;
  }

  function save() {
    if (validate()) onSave({ ...techData, number: editMode ? techData.number : form.number, name: form.name, specialties: form.specialties });
  }

  function removeSpecialty(s) { setForm(prev => ({ ...prev, specialties: prev.specialties.filter(x => x !== s) })); }
  function addSpecialty(s) {
    if (!form.specialties.includes(s)) setForm(prev => ({ ...prev, specialties: [...prev.specialties, s] }));
    setPickingCategory(false);
  }

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, width: '420px' }}>
        <div style={S.header}>
          <span style={S.title}>{editMode ? 'Edit Technician' : 'Add Technician'}</span>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={S.row}>
          <div style={{ width: '100px', flexShrink: 0 }}>
            <label style={S.label}>Tech #</label>
            <input style={{ ...S.input, border: errors.number ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
              name="number" value={form.number} onChange={change} disabled={editMode} placeholder="e.g. 3" />
            {errors.number && <div style={S.err}>{errors.number}</div>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Name</label>
            <input style={{ ...S.input, border: errors.name ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
              name="name" value={form.name} onChange={change} placeholder="Full name" />
            {errors.name && <div style={S.err}>{errors.name}</div>}
          </div>
        </div>

        <div style={S.field}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <label style={{ ...S.label, marginBottom: 0 }}>Specialties</label>
            <button type="button" onClick={() => setPickingCategory(true)}
              style={{ padding: '4px 10px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '4px', color: '#334155', fontSize: '11px', cursor: 'pointer' }}>
              + Add
            </button>
          </div>
          {errors.specialties && <div style={S.err}>{errors.specialties}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {form.specialties.map(s => (
              <span key={s} style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '12px',
                padding: '3px 10px', fontSize: '11px', color: '#334155',
              }}>
                {s}
                <button onClick={() => removeSpecialty(s)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #cbd5e1', paddingTop: '14px' }}>
          <button onClick={onClose}
            style={{ padding: '7px 16px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#ffffff', color: '#334155', cursor: 'pointer', fontSize: '13px' }}>
            Cancel
          </button>
          <button onClick={save}
            style={{ padding: '7px 18px', background: '#f59e0b', color: '#0f172a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
            {editMode ? 'Save Changes' : 'Add Technician'}
          </button>
        </div>

        {pickingCategory && (
          <div style={{ ...S.overlay, zIndex: 1100 }}>
            <div style={{ ...S.modal, width: '420px' }}>
              <RepairCategories
                selectedCategory="" selectedSubcategory=""
                onChange={({ category }) => addSpecialty(category)}
                onClose={() => setPickingCategory(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function TechnicianModal({ onClose }) {
  const [technicians, setTechnicians] = useState({});
  const [showAdd, setShowAdd]         = useState(false);
  const [editing, setEditing]         = useState(null);

  useEffect(() => {
    client.graphql({ query: queries.listTechnicians })
      .then(r => {
        const map = r.data.listTechnicians.items.reduce((acc, t) => ({
          ...acc,
          [t.number]: { id: t.id, number: t.number, name: t.name, specialties: t.specialties || [], currentLoad: t.currentLoad || 0, completionRate: t.completionRate || 100 },
        }), {});
        setTechnicians(map);
      })
      .catch(err => console.error('Error fetching technicians:', err));
  }, []);

  async function handleAdd(data) {
    try {
      const r = await client.graphql({ query: mutations.createTechnician, variables: { input: { number: data.number, name: data.name, specialties: data.specialties, currentLoad: 0, completionRate: 100, active: true, locationId: DEFAULT_LOCATION_ID } } });
      const t = r.data.createTechnician;
      setTechnicians(prev => ({ ...prev, [t.number]: { id: t.id, number: t.number, name: t.name, specialties: t.specialties, currentLoad: t.currentLoad, completionRate: t.completionRate } }));
      setShowAdd(false);
    } catch (e) { console.error(e); }
  }

  async function handleUpdate(data) {
    try {
      const r = await client.graphql({ query: mutations.updateTechnician, variables: { input: { id: data.id, name: data.name, specialties: data.specialties } } });
      const t = r.data.updateTechnician;
      setTechnicians(prev => ({ ...prev, [t.number]: { ...prev[t.number], ...t } }));
      setEditing(null);
    } catch (e) { console.error(e); }
  }

  async function handleDelete(number) {
    if (!window.confirm(`Remove technician #${number}?`)) return;
    try {
      await client.graphql({ query: mutations.deleteTechnician, variables: { input: { id: technicians[number].id } } });
      setTechnicians(prev => { const n = { ...prev }; delete n[number]; return n; });
    } catch (e) { console.error(e); }
  }

  const techList = Object.values(technicians).sort((a, b) => Number(a.number) - Number(b.number));

  return (
    <div style={S.overlay}>
      <div style={S.modal}>

        <div style={S.header}>
          <span style={S.title}>Technician Management</span>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        <button onClick={() => setShowAdd(true)}
          style={{ marginBottom: '18px', padding: '8px 16px', background: '#f59e0b', color: '#0f172a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          + Add Technician
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
          {techList.map(tech => (
            <div key={tech.number} style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{tech.name}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>#{tech.number}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => setEditing(tech)}
                    style={{ padding: '4px 10px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '4px', color: '#334155', fontSize: '11px', cursor: 'pointer' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(tech.number)}
                    style={{ padding: '4px 10px', background: 'transparent', border: '1px solid #7f1d1d', borderRadius: '4px', color: '#ef4444', fontSize: '11px', cursor: 'pointer' }}>
                    Remove
                  </button>
                </div>
              </div>
              {tech.specialties?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {tech.specialties.map(s => (
                    <span key={s} style={{ background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: '10px', fontSize: '10px' }}>{s}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {!techList.length && (
          <div style={{ color: '#64748b', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', padding: '24px 0' }}>No technicians yet</div>
        )}

      </div>

      {showAdd  && <TechForm onClose={() => setShowAdd(false)}  onSave={handleAdd}    existing={technicians} />}
      {editing  && <TechForm onClose={() => setEditing(null)}   onSave={handleUpdate} existing={technicians} editMode techData={editing} />}
    </div>
  );
}
